import { useCallback, useMemo, useState } from 'react';
import { Button, Form, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  collectExpandableNodeIds,
  countNavigationDocLeaves,
  filterNavigationByDocIds,
  searchWikiDocuments,
} from '../../../app/helpers/documentationWikiHelper';

const DocsSidebar = ({
  navigationTree,
  roleLogin,
  configurationState,
  collapsed,
  onToggleCollapse,
}) => {
  const { t } = useTranslation();
  const { docId } = useParams();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [matchingDocIds, setMatchingDocIds] = useState(() => new Set());
  const [expandedNodes, setExpandedNodes] = useState(() => new Set());

  const allExpandableIds = useMemo(() => {
    const ids = [];
    (navigationTree || []).forEach((root) => {
      ids.push(root.id);
      collectExpandableNodeIds(root.children || [], ids);
    });
    return ids;
  }, [navigationTree]);

  const displayTree = useMemo(() => {
    if (!navigationTree) return [];

    if (searchActive && matchingDocIds.size > 0) {
      return navigationTree
        .map((root) => {
          const children = filterNavigationByDocIds(root.children || [], matchingDocIds);
          if (children.length === 0) return null;
          return { ...root, children };
        })
        .filter(Boolean);
    }

    return navigationTree;
  }, [navigationTree, searchActive, matchingDocIds]);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedNodes(new Set(allExpandableIds));
  }, [allExpandableIds]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  const handleRunSearch = useCallback(() => {
    const { count, matchingDocIds: ids } = searchWikiDocuments(roleLogin, searchQuery, configurationState);
    setMatchCount(count);
    setMatchingDocIds(ids);
    setSearchActive(true);

    if (count > 0) {
      const expandIds = [];
      (navigationTree || []).forEach((root) => {
        const filteredChildren = filterNavigationByDocIds(root.children || [], ids);
        if (filteredChildren.length > 0) {
          expandIds.push(root.id);
          collectExpandableNodeIds(filteredChildren, expandIds);
        }
      });
      setExpandedNodes(new Set(expandIds));
    }
  }, [configurationState, navigationTree, roleLogin, searchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchActive(false);
    setMatchCount(0);
    setMatchingDocIds(new Set());
    setExpandedNodes(new Set());
  }, []);

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRunSearch();
    }
  };

  const toggleSearchPanel = () => {
    setSearchOpen((prev) => {
      if (prev) {
        handleClearSearch();
      }
      return !prev;
    });
  };

  const isExpanded = (nodeId) => expandedNodes.has(nodeId);

  const renderNavNodes = (nodes, depth = 0) =>
    nodes.map((node) => {
      if (node.docId) {
        const isActive = node.docId === docId;
        return (
          <Nav.Link
            key={node.id}
            as={Link}
            to={`/documentation/${node.docId}`}
            className={`docs-nav-leaf ${isActive ? 'active' : ''}`}
            style={{ paddingLeft: `${0.5 + depth * 0.65}rem` }}
          >
            {node.label}
          </Nav.Link>
        );
      }

      const childCount = countNavigationDocLeaves(node.children || []);
      const expanded = isExpanded(node.id);

      return (
        <div key={node.id} className="docs-nav-group">
          <button
            type="button"
            className="docs-nav-group-toggle"
            onClick={() => toggleNode(node.id)}
            aria-expanded={expanded}
          >
            <i className={`fa fa-${expanded ? 'minus' : 'plus'}-square-o docs-nav-toggle-icon`} />
            <span className="docs-nav-group-title-text">{node.label}</span>
            <span className="docs-nav-count">({childCount})</span>
          </button>
          {expanded && (
            <div className="docs-nav-group-children">
              {renderNavNodes(node.children || [], depth + 1)}
            </div>
          )}
        </div>
      );
    });

  if (collapsed) {
    return null;
  }

  return (
    <aside className="docs-sidebar docs-no-print">
      <div className="docs-sidebar-header">
        <span className="docs-sidebar-header-title">{t('docs.sidebarTitle')}</span>
        <div className="docs-sidebar-toolbar">
          <button
            type="button"
            className={`docs-sidebar-icon-btn ${searchOpen ? 'active' : ''}`}
            onClick={toggleSearchPanel}
            title={t('docs.searchToggle')}
            aria-label={t('docs.searchToggle')}
          >
            <i className="fa fa-search" />
          </button>
          <button
            type="button"
            className="docs-sidebar-icon-btn"
            onClick={expandAll}
            title={t('docs.expandAll')}
            aria-label={t('docs.expandAll')}
          >
            <i className="fa fa-expand" />
          </button>
          <button
            type="button"
            className="docs-sidebar-icon-btn"
            onClick={collapseAll}
            title={t('docs.collapseAll')}
            aria-label={t('docs.collapseAll')}
          >
            <i className="fa fa-compress" />
          </button>
          <button
            type="button"
            className="docs-sidebar-icon-btn"
            onClick={onToggleCollapse}
            title={t('docs.sidebarCollapse')}
            aria-label={t('docs.sidebarCollapse')}
          >
            <i className="fa fa-angle-double-left" />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="docs-sidebar-search">
          <div className="docs-sidebar-search-row">
            <Form.Control
              type="search"
              size="sm"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t('docs.searchPlaceholder')}
              aria-label={t('docs.searchPlaceholder')}
            />
            <Button
              variant="primary"
              size="sm"
              className="docs-sidebar-search-btn"
              onClick={handleRunSearch}
            >
              {t('docs.searchRun')}
            </Button>
          </div>
          {searchActive && (
            <p className="docs-sidebar-search-results">
              {matchCount === 0
                ? t('docs.searchNoResults')
                : t('docs.searchResultsCount', { count: matchCount })}
            </p>
          )}
        </div>
      )}

      {!displayTree || displayTree.length === 0 ? (
        <p className="docs-empty-nav">
          {searchActive ? t('docs.searchNoResults') : t('docs.emptyNavigation')}
        </p>
      ) : (
        displayTree.map((root) => {
          const roleLabel = root.labelKey ? t(root.labelKey) : root.label;
          const pageCount = countNavigationDocLeaves(root.children || []);
          const rootExpanded = isExpanded(root.id);

          return (
            <div key={root.id} className="docs-sidebar-role-block">
              <button
                type="button"
                className="docs-sidebar-role-toggle"
                onClick={() => toggleNode(root.id)}
                aria-expanded={rootExpanded}
              >
                <i className={`fa fa-${rootExpanded ? 'minus' : 'plus'}-square-o docs-nav-toggle-icon`} />
                <h3 className="docs-sidebar-role-title">
                  {roleLabel}
                  <span className="docs-nav-count"> ({pageCount})</span>
                </h3>
              </button>
              {rootExpanded && (
                <div className="docs-sidebar-role-children">
                  {renderNavNodes(root.children || [])}
                </div>
              )}
            </div>
          );
        })
      )}
    </aside>
  );
};

export default DocsSidebar;
